import type { CSSProperties, ReactNode } from 'react';
import { COULEURS, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { libelleArrivee } from './apercu-lien.js';
import { resumePiece } from './boite.js';
import type { EtatChargement, EtatRattachement } from './controleurs.js';
import type { Controle } from './controles-modele.js';
import { MODELES, type ApercuLien, type Fil, type FilDetail, type ModeleMessage, type PlanRelance, type Simulation } from './contrat.js';

const carte: CSSProperties = {
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  padding: 16,
  background: COULEURS.blanc,
  margin: '12px 0',
};
const liste: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

const formatDate = (instant: string): string =>
  new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/Guadeloupe' }).format(new Date(instant));

const ETATS: Readonly<Record<PlanRelance['state'], string>> = { actif: 'Actif', pause: 'En pause', arrete: 'Arrêté', termine: 'Terminé' };
const CANAUX: Readonly<Record<string, string>> = { whatsapp: 'WhatsApp', email: 'e-mail', sms: 'SMS', postal: 'courrier' };
const ACTIONS: Readonly<Record<string, string>> = {
  message: 'Message',
  appel: 'Appel',
  decision: 'Décision à prendre',
  recurrent_mensuel: 'Rappel mensuel',
};

/** Aperçu du lien tel que le destinataire le verra, avec la page sur laquelle il arrive. */
export function VueApercuLien({ apercu }: { apercu: ApercuLien }) {
  return (
    <figure data-apercu-lien={apercu.landing} style={{ ...carte, margin: '12px 0' }}>
      <figcaption style={{ color: COULEURS.ardoise, fontSize: 14 }}>{apercu.siteName}</figcaption>
      <p style={{ color: COULEURS.marine, fontWeight: 600, margin: '4px 0' }}>{apercu.title}</p>
      <p style={{ margin: '4px 0' }}>{apercu.description}</p>
      <p style={{ margin: '4px 0', color: COULEURS.ardoise }}>{`Page d’arrivée : ${libelleArrivee(apercu.landing)}`}</p>
    </figure>
  );
}

const ETAT_TRANSCRIPTION: Readonly<Record<'a_verifier' | 'relu', string>> = { a_verifier: 'À vérifier', relu: 'Relu' };

/** Nom affiché : un message sortant est signé du Cabinet, un entrant sans nom vient du correspondant du fil. */
export function expediteur(m: { direction: 'entrant' | 'sortant'; senderDisplayName: string | null }, correspondant: string): string {
  if (m.senderDisplayName !== null) return m.senderDisplayName;
  return m.direction === 'sortant' ? 'Cabinet MAAT' : correspondant.trim() === '' ? 'Expéditeur inconnu' : correspondant;
}

/** Un fil de la boîte unifiée : messages, pièces avec aperçu, et ce que l'outil a compris des vocaux. */
export function VueFil({ fil }: { fil: FilDetail }) {
  return (
    <section data-fil={fil.id} aria-label="Fil de discussion">
      <h2 style={{ color: COULEURS.marine, fontSize: 20 }}>{fil.channelAccount.label}</h2>
      <p style={{ color: COULEURS.ardoise }}>{fil.externalContact}</p>
      <ul style={liste}>
        {fil.messages.map((m) => (
          <li key={m.id} data-sens={m.direction} style={carte}>
            <p style={{ fontWeight: 600, margin: 0 }}>{expediteur(m, fil.externalContact)}</p>
            <p style={{ color: COULEURS.ardoise, margin: '2px 0 8px', fontSize: 14 }}>{formatDate(m.sentAt)}</p>
            {m.body === '' ? null : <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>}
            {m.attachments.length === 0 ? null : (
              <ul style={liste} aria-label="Pièces jointes">
                {m.attachments.map((p) => (
                  <li key={p.documentVersionId} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
                    {p.thumbnailUrl === null ? null : (
                      <img src={p.thumbnailUrl} alt="" width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    )}
                    <span>{resumePiece(p)}</span>
                  </li>
                ))}
              </ul>
            )}
            {m.transcript === null ? null : (
              <div
                data-transcription={m.transcript.status}
                style={{ background: COULEURS.surface, borderRadius: 8, padding: 12, marginTop: 8 }}
              >
                <p style={{ fontWeight: 600, margin: 0 }}>Ce que l’outil a compris</p>
                <p style={{ margin: '4px 0' }}>{m.transcript.text}</p>
                <p style={{ color: COULEURS.ambre, margin: 0 }}>{ETAT_TRANSCRIPTION[m.transcript.status]}</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface PropsVueModele {
  modele: ModeleMessage;
  corps: string;
  controles: readonly Controle[];
  onChanger?: (corps: string) => void;
  onEnregistrer?: () => void;
}

/** Éditeur d'un modèle : les contrôles s'affichent en direct et l'enregistrement est refusé tant qu'un point bloque. */
export function VueModele({ modele, corps, controles, onChanger, onEnregistrer }: PropsVueModele) {
  const libelle = MODELES.find((m) => m.code === modele.code)?.libelle ?? modele.code;
  const bloque = controles.some((c) => c.bloquant);
  return (
    <section data-modele={modele.code} aria-label={`Modèle ${modele.code}`} style={carte}>
      <h3 style={{ color: COULEURS.marine, margin: 0 }}>{`Modèle ${modele.code} : ${libelle}`}</h3>
      <p
        style={{ color: COULEURS.ardoise }}
      >{`Version ${modele.versionNo}, ${modele.status === 'valide_isabelle' ? 'validée par Isabelle' : 'brouillon'}`}</p>
      <label>
        <span style={{ display: 'block', fontWeight: 600 }}>Texte du modèle</span>
        <textarea
          value={corps}
          rows={6}
          style={{ width: '100%', font: 'inherit', padding: 8, boxSizing: 'border-box' }}
          {...(onChanger ? { onChange: (e) => onChanger(e.target.value) } : { readOnly: true })}
        />
      </label>
      {controles.length === 0 ? null : (
        <ul role="alert" style={{ ...liste, color: COULEURS.rouge }}>
          {controles.map((c) => (
            <li key={c.code} data-controle={c.code}>
              {c.message}
            </li>
          ))}
        </ul>
      )}
      <button type="button" disabled={bloque} onClick={onEnregistrer} style={bloque ? styleBouton : styleBoutonPrincipal}>
        Enregistrer une nouvelle version
      </button>
    </section>
  );
}

export interface PropsVueRelances {
  plans: readonly PlanRelance[];
  simulation: Simulation | null;
  onPause?: (plan: PlanRelance) => void;
  onReprise?: (plan: PlanRelance) => void;
  onSimuler?: (plan: PlanRelance) => void;
}

/** Plans de relance, pause « on laisse respirer », reprise et simulation de la cadence. */
export function VueRelances({ plans, simulation, onPause, onReprise, onSimuler }: PropsVueRelances) {
  return (
    <section aria-label="Plans de relance">
      <ul style={liste}>
        {plans.map((plan) => (
          <li key={plan.id} data-plan={plan.id} style={carte}>
            <p style={{ color: COULEURS.marine, fontWeight: 600, margin: 0 }}>{plan.cadenceName}</p>
            <p style={{ margin: '4px 0' }}>{ETATS[plan.state]}</p>
            {plan.nextAction === null || plan.nextActionAt === null ? null : (
              <p style={{ margin: '4px 0' }}>
                {`Prochaine action : ${ACTIONS[plan.nextAction.kind]}, ${formatDate(plan.nextActionAt)}`}
                {plan.nextAction.templateCode === null ? '' : `, Modèle ${plan.nextAction.templateCode}`}
                {plan.nextAction.channel === null ? '' : ` (${CANAUX[plan.nextAction.channel]})`}
              </p>
            )}
            {plan.state === 'pause' ? (
              <>
                <p style={{ margin: '4px 0' }}>{`${plan.pausedReason ?? ''}${plan.pausedUntil ? `, jusqu’au ${plan.pausedUntil}` : ''}`}</p>
                <button type="button" onClick={() => onReprise?.(plan)} style={styleBoutonPrincipal}>
                  Reprendre les relances
                </button>
              </>
            ) : plan.state === 'actif' ? (
              <button type="button" onClick={() => onPause?.(plan)} style={styleBouton}>
                On laisse respirer
              </button>
            ) : null}{' '}
            <button type="button" onClick={() => onSimuler?.(plan)} style={styleBouton}>
              Simuler la cadence
            </button>
          </li>
        ))}
      </ul>
      {simulation === null ? null : (
        <div data-simulation style={carte}>
          <h3 style={{ color: COULEURS.marine, margin: 0 }}>Simulation, rien n’est programmé</h3>
          <ol>
            {simulation.actions.map((a) => (
              <li key={`${a.at}-${a.kind}`}>
                {`${formatDate(a.at)} : ${ACTIONS[a.kind]}${a.templateCode === null ? '' : `, Modèle ${a.templateCode}`}`}
                {a.blockedBy === null ? '' : ` (bloqué : ${a.blockedBy})`}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

export interface PropsVueEtatListe {
  etat: EtatChargement;
  nombre: number;
  chargement: string;
  vide: string;
  onReessayer?: () => unknown;
  children?: ReactNode;
}

/** Trois états distincts d'une liste : chargement, erreur avec « Réessayer », vide réel. */
export interface PropsBoutonEnvoyer {
  enCours: boolean;
  onEnvoyer: () => void;
}

/** Bouton d'envoi : désactivé pendant l'appel pour éviter tout double envoi. */
export function BoutonEnvoyer({ enCours, onEnvoyer }: PropsBoutonEnvoyer) {
  return (
    <button type="button" disabled={enCours} onClick={onEnvoyer} style={enCours ? styleBouton : styleBoutonPrincipal}>
      {enCours ? 'Envoi en cours…' : 'Envoyer'}
    </button>
  );
}

export function VueEtatListe({ etat, nombre, chargement, vide, onReessayer, children }: PropsVueEtatListe) {
  if (etat === 'chargement') return <p role="status">{chargement}</p>;
  if (etat === 'erreur') {
    return (
      <div role="alert">
        <p style={{ color: COULEURS.rouge }}>Cette liste n’a pas pu être chargée.</p>
        <button type="button" onClick={onReessayer} style={styleBouton}>
          Réessayer
        </button>
      </div>
    );
  }
  return nombre === 0 ? <p>{vide}</p> : <>{children}</>;
}

export interface PropsVueRattachement {
  fil: Fil;
  rattachement: EtatRattachement | null;
  onDemarrer?: () => unknown;
  onChoisirDossier?: (dossierId: string) => unknown;
  onChercher?: (recherche: string) => unknown;
  onRattacher?: (personId: string | null) => unknown;
  onAnnuler?: () => void;
}

/** Rattachement d'un fil non rattaché à un dossier, puis à une personne ou à un organisme. */
export function VueRattachement({
  fil,
  rattachement,
  onDemarrer,
  onChoisirDossier,
  onChercher,
  onRattacher,
  onAnnuler,
}: PropsVueRattachement) {
  if (fil.status !== 'non_rattache') return null;
  if (rattachement === null) {
    return (
      <p>
        <button type="button" onClick={onDemarrer} style={styleBoutonPrincipal}>
          Rattacher à un dossier
        </button>
      </p>
    );
  }
  return (
    <section aria-label="Rattacher ce fil" style={carte}>
      <h3 style={{ color: COULEURS.marine, margin: 0 }}>Rattacher ce fil</h3>
      {rattachement.dossiers === null ? <p role="status">Chargement des dossiers…</p> : null}
      {rattachement.dossiers?.length === 0 ? <p>Aucun dossier à proposer.</p> : null}
      {rattachement.dossiers && rattachement.dossiers.length > 0 ? (
        <label>
          <span style={{ display: 'block', fontWeight: 600 }}>Dossier</span>
          <select value={rattachement.dossierId ?? ''} onChange={(e) => onChoisirDossier?.(e.target.value)} style={{ minHeight: 44 }}>
            <option value="" disabled>
              Choisir un dossier
            </option>
            {rattachement.dossiers.map((d) => (
              <option key={d.id} value={d.id}>
                {`${d.reference} · ${d.title}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {rattachement.dossierId === null ? null : (
        <>
          <label>
            <span style={{ display: 'block', fontWeight: 600 }}>Rechercher la personne (2 lettres au moins)</span>
            <input value={rattachement.recherche} onChange={(e) => onChercher?.(e.target.value)} style={{ minHeight: 44 }} />
          </label>
          <ul style={liste}>
            {(rattachement.personnes ?? []).map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => onRattacher?.(p.id)} style={{ ...styleBouton, width: '100%', textAlign: 'left' }}>
                  {`${p.displayName}${p.relationLabel === null ? '' : `, ${p.relationLabel}`}`}
                </button>
              </li>
            ))}
          </ul>
          {rattachement.personnes?.length === 0 ? <p>Aucune personne trouvée dans ce dossier.</p> : null}
          <button type="button" onClick={() => onRattacher?.(null)} style={styleBouton}>
            Organisme (aucune personne)
          </button>
        </>
      )}{' '}
      <button type="button" onClick={onAnnuler} style={styleBouton}>
        Annuler
      </button>
    </section>
  );
}
